import * as XLSX from 'xlsx';

export interface SimpleBiomarkerResult {
  biomarkerName: string;
  value: string;
  summary: string;
  isBinary?: boolean;
  binaryValue?: 'POSITIVE' | 'NEGATIVE';
}

export interface SimpleCSVParseResult {
  success: boolean;
  data: SimpleBiomarkerResult[];
  errors: string[];
}

export const parseSimpleBiomarkerFile = async (file: File): Promise<SimpleCSVParseResult> => {
  const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  
  if (fileExtension === '.xlsx' || fileExtension === '.xls') {
    return parseSimpleXLSX(file);
  } else {
    const csvContent = await file.text();
    return parseSimpleCSV(csvContent);
  }
};

export const parseSimpleXLSX = async (file: File): Promise<SimpleCSVParseResult> => {
  const errors: string[] = [];
  const data: SimpleBiomarkerResult[] = [];
  
  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
      header: 1,
      raw: false
    });
    
    if (jsonData.length < 2) {
      return {
        success: false,
        data: [],
        errors: ['Excel file must contain at least a header row and one data row']
      };
    }

    const headers = (jsonData[0] as string[]).map(h => h?.toString().trim().toLowerCase().replace(/"/g, '') || '');
    
    // Find column indices for the 3 required columns
    const biomarkerNameIndex = findColumnIndex(headers, ['biomarker name', 'biomarker_name', 'name', 'test name', 'testname']);
    const valueIndex = findColumnIndex(headers, ['value', 'result', 'test result', 'test_result']);
    const summaryIndex = findColumnIndex(headers, ['summary', 'summery', 'notes', 'comment', 'comments']);

    if (biomarkerNameIndex === -1 || valueIndex === -1) {
      return {
        success: false,
        data: [],
        errors: ['Excel file must contain "Biomarker Name" and "Value" columns']
      };
    }

    for (let i = 1; i < jsonData.length; i++) {
      const row = jsonData[i] as any[];
      
      if (!row || row.length === 0) continue;
      
      const biomarkerName = row[biomarkerNameIndex]?.toString().trim();
      const value = row[valueIndex]?.toString().trim();
      const summary = summaryIndex !== -1 ? (row[summaryIndex]?.toString().trim() || '') : '';

      if (!biomarkerName || !value) {
        errors.push(`Row ${i + 1}: Missing biomarker name or value`);
        continue;
      }

      // Check if this is a binary value
      const isBinary = isBinaryValue(value);
      const binaryValue = isBinary ? getBinaryValue(value) : null;


      data.push({
        biomarkerName,
        value,
        summary,
        isBinary,
        binaryValue: binaryValue || undefined
      });
    }

    return {
      success: errors.length === 0,
      data,
      errors
    };
  } catch (error) {
    return {
      success: false,
      data: [],
      errors: ['Failed to parse Excel file: ' + (error as Error).message]
    };
  }
};

export const parseSimpleCSV = (csvContent: string): SimpleCSVParseResult => {
  const errors: string[] = [];
  const data: SimpleBiomarkerResult[] = [];
  
  try {
    const lines = csvContent.split('\n').filter(line => line.trim() !== '');
    
    if (lines.length < 2) {
      return {
        success: false,
        data: [],
        errors: ['CSV file must contain at least a header row and one data row']
      };
    }

    const headers = parseCSVRow(lines[0]).map(h => h.trim().toLowerCase().replace(/"/g, ''));
    
    // Find column indices for the 3 required columns
    const biomarkerNameIndex = findColumnIndex(headers, ['biomarker name', 'biomarker_name', 'name', 'test name', 'testname']);
    const valueIndex = findColumnIndex(headers, ['value', 'result', 'test result', 'test_result']);
    const summaryIndex = findColumnIndex(headers, ['summary', 'summery', 'notes', 'comment', 'comments']);

    if (biomarkerNameIndex === -1 || valueIndex === -1) {
      return {
        success: false,
        data: [],
        errors: ['CSV file must contain "Biomarker Name" and "Value" columns']
      };
    }

    for (let i = 1; i < lines.length; i++) {
      const row = parseCSVRow(lines[i]);
      
      if (row.length !== headers.length) {
        errors.push(`Row ${i + 1}: Column count mismatch`);
        continue;
      }

      const biomarkerName = row[biomarkerNameIndex]?.trim();
      const value = row[valueIndex]?.trim();
      const summary = summaryIndex !== -1 ? (row[summaryIndex]?.trim() || '') : '';

      if (!biomarkerName || !value) {
        errors.push(`Row ${i + 1}: Missing biomarker name or value`);
        continue;
      }

      // Check if this is a binary value
      const isBinary = isBinaryValue(value);
      const binaryValue = isBinary ? getBinaryValue(value) : null;


      data.push({
        biomarkerName,
        value,
        summary,
        isBinary,
        binaryValue: binaryValue || undefined
      });
    }

    return {
      success: errors.length === 0,
      data,
      errors
    };
  } catch (error) {
    return {
      success: false,
      data: [],
      errors: ['Failed to parse CSV file: ' + (error as Error).message]
    };
  }
};

const findColumnIndex = (headers: string[], possibleNames: string[]): number => {
  for (const name of possibleNames) {
    const index = headers.findIndex(h => h === name);
    if (index !== -1) return index;
  }
  return -1;
};

const isBinaryValue = (value: string): boolean => {
  const normalizedValue = value.toLowerCase().trim();
  return normalizedValue === 'positive' || normalizedValue === 'negative' || 
         normalizedValue === 'pos' || normalizedValue === 'neg' ||
         normalizedValue === 'yes' || normalizedValue === 'no' ||
         normalizedValue === 'true' || normalizedValue === 'false';
};

const getBinaryValue = (value: string): 'POSITIVE' | 'NEGATIVE' | null => {
  const normalizedValue = value.toLowerCase().trim();
  
  if (normalizedValue === 'positive' || normalizedValue === 'pos' || 
      normalizedValue === 'yes' || normalizedValue === 'true') {
    return 'POSITIVE';
  }
  
  if (normalizedValue === 'negative' || normalizedValue === 'neg' || 
      normalizedValue === 'no' || normalizedValue === 'false') {
    return 'NEGATIVE';
  }
  
  return null;
};

const parseCSVRow = (row: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < row.length; i++) {
    const char = row[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current.trim());
  return result;
};

export const generateSimpleTemplate = (): string => {
  const headers = ['Biomarker Name', 'Value', 'Summary'];
  const sampleData = [
    ['Glucose', '95', 'Fasting'],
    ['Cholesterol', '180', 'Normal'],
    ['HDL', '45', 'Good'],
    ['LDL', '110', 'High'],
    ['Triglycerides', '150', 'Normal']
  ];

  const csvContent = [
    headers.join(','),
    ...sampleData.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');

  return csvContent;
};